import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import {
  Truck,
  MapPin,
  Clock,
  Phone,
  User,
  Package,
  CheckCircle2,
  Circle,
  Loader2,
  Calendar,
  Navigation,
  Star,
} from 'lucide-react';
import axios from 'axios';
import { useCountry } from '../../context/CountryContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Delivery status timeline component
export const DeliveryTimeline = ({ timeline }) => {
  const { formatCurrency } = useCountry();
  return (
    <div className="space-y-4">
      {timeline?.map((step, index) => (
        <div key={step.status} className="flex gap-3">
          <div className="flex flex-col items-center">
            {step.state === 'completed' ? (
              <CheckCircle2 className="h-6 w-6 text-green-500" />
            ) : step.state === 'current' ? (
              <div className="h-6 w-6 rounded-full border-2 border-blue-500 bg-blue-100 flex items-center justify-center">
                <div className="h-2 w-2 rounded-full bg-blue-500 animate-pulse" />
              </div>
            ) : (
              <Circle className="h-6 w-6 text-gray-300" />
            )}
            {index < timeline.length - 1 && (
              <div className={`w-0.5 h-8 ${step.state === 'completed' ? 'bg-green-300' : 'bg-gray-200'}`} />
            )}
          </div>
          <div className="flex-1 pb-4">
            <p className={`font-medium ${step.state === 'current' ? 'text-blue-600' : step.state === 'completed' ? 'text-green-600' : 'text-gray-400'}`}>
              {step.label}
            </p>
            <p className="text-sm text-gray-500">{step.description}</p>
            {step.timestamp && (
              <p className="text-xs text-gray-400 mt-1">
                {new Date(step.timestamp).toLocaleString()}
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

// Delivery tracking component
export const DeliveryTracker = ({ orderId }) => {
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTracking = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/api/delivery/track/${orderId}`);
        setTracking(response.data);
        setError(null);
      } catch (err) {
        setError('Failed to load tracking info');
      } finally {
        setLoading(false);
      }
    };

    if (orderId) {
      fetchTracking();
      // Poll every 30 seconds for updates
      const interval = setInterval(fetchTracking, 30000);
      return () => clearInterval(interval);
    }
  }, [orderId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-500 p-4">
        {error}
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Truck className="h-5 w-5 text-cyan-500" />
          Delivery Tracking
        </CardTitle>
        <div className="flex items-center gap-2 mt-2">
          <Badge variant="outline" className="text-sm">
            Order: {tracking?.order_id}
          </Badge>
          <Badge 
            className={
              tracking?.current_status === 'delivered' ? 'bg-green-100 text-green-700' :
              tracking?.current_status === 'cancelled' ? 'bg-red-100 text-red-700' :
              'bg-blue-100 text-blue-700'
            }
          >
            {tracking?.status_label}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        {/* Progress bar */}
        <div className="mb-6">
          <div className="flex justify-between text-sm text-gray-500 mb-1">
            <span>Progress</span>
            <span>{tracking?.progress_percent}%</span>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-cyan-500 to-teal-500 transition-all duration-500"
              style={{ width: `${tracking?.progress_percent || 0}%` }}
            />
          </div>
        </div>

        {/* Driver info */}
        {(tracking?.pickup?.driver || tracking?.delivery?.driver) && (
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <h4 className="font-medium mb-2">Driver Info</h4>
            {tracking?.current_status?.includes('pickup') && tracking?.pickup?.driver && (
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-cyan-100 flex items-center justify-center">
                  <User className="h-5 w-5 text-cyan-600" />
                </div>
                <div>
                  <p className="font-medium">{tracking.pickup.driver.name}</p>
                  <p className="text-sm text-gray-500">{tracking.pickup.driver.phone}</p>
                </div>
                <Button variant="outline" size="sm" className="ml-auto">
                  <Phone className="h-4 w-4 mr-1" />
                  Call
                </Button>
              </div>
            )}
            {tracking?.current_status?.includes('delivery') && tracking?.delivery?.driver && (
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                  <User className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="font-medium">{tracking.delivery.driver.name}</p>
                  <p className="text-sm text-gray-500">{tracking.delivery.driver.phone}</p>
                </div>
                <Button variant="outline" size="sm" className="ml-auto">
                  <Phone className="h-4 w-4 mr-1" />
                  Call
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Timeline */}
        <DeliveryTimeline timeline={tracking?.timeline} />
      </CardContent>
    </Card>
  );
};

// Delivery provider selector
export const DeliveryProviderSelector = ({ onSelect, selectedId }) => {
  const { formatCurrency } = useCountry();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/delivery/providers?country=SL`);
        setProviders(response.data.providers || []);
      } catch (err) {
        console.error('Failed to load delivery providers:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProviders();
  }, []);

  if (loading) {
    return <div className="text-center py-4"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;
  }

  return (
    <div className="space-y-3">
      {providers.map((provider) => (
        <div
          key={provider.id}
          onClick={() => onSelect(provider)}
          className={`p-4 border rounded-lg cursor-pointer transition-all ${
            selectedId === provider.id 
              ? 'border-cyan-500 bg-cyan-50' 
              : 'border-gray-200 hover:border-cyan-300'
          }`}
        >
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2">
                <h4 className="font-medium">{provider.provider_name}</h4>
                {provider.is_third_party && (
                  <Badge variant="outline" className="text-xs">3rd Party</Badge>
                )}
                {provider.is_sponsored && (
                  <Badge className="bg-cyan-100 text-cyan-700 text-xs">Recommended</Badge>
                )}
              </div>
              <p className="text-sm text-gray-500 mt-1">{provider.description}</p>
              <div className="flex items-center gap-3 mt-2 text-sm">
                <span className="flex items-center gap-1">
                  <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                  {provider.rating?.toFixed(1)}
                </span>
                <span className="text-gray-400">|</span>
                <span className="text-gray-500">From {formatCurrency(provider.starting_price)}</span>
              </div>
            </div>
            {selectedId === provider.id && (
              <CheckCircle2 className="h-6 w-6 text-cyan-500" />
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

// Main delivery booking dialog
export const DeliveryBookingDialog = ({ 
  laundryBookingId, 
  laundryProvider, 
  trigger,
  onSuccess 
}) => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [useProviderDelivery, setUseProviderDelivery] = useState(true);
  const [timeSlots, setTimeSlots] = useState([]);
  
  const [formData, setFormData] = useState({
    pickup_address: {
      address_line1: '',
      address_line2: '',
      city: 'Colombo',
      landmark: '',
      contact_name: '',
      contact_phone: '',
    },
    delivery_type: 'pickup_and_delivery',
    preferred_pickup_date: '',
    preferred_pickup_time_slot: '',
    preferred_delivery_date: '',
    preferred_delivery_time_slot: '',
    special_instructions: '',
    items_description: '',
  });

  useEffect(() => {
    // Load time slots when date changes
    if (formData.preferred_pickup_date) {
      fetchTimeSlots(formData.preferred_pickup_date);
    }
  }, [formData.preferred_pickup_date]);

  const fetchTimeSlots = async (date) => {
    try {
      const response = await axios.get(`${API_URL}/api/delivery/time-slots?date=${date}`);
      setTimeSlots(response.data.slots || []);
    } catch (err) {
      console.error('Failed to load time slots:', err);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const orderData = {
        laundry_booking_id: laundryBookingId || `LAUNDRY-${Date.now()}`,
        pickup_address: formData.pickup_address,
        delivery_type: formData.delivery_type,
        preferred_pickup_date: formData.preferred_pickup_date,
        preferred_pickup_time_slot: formData.preferred_pickup_time_slot,
        preferred_delivery_date: formData.preferred_delivery_date || formData.preferred_pickup_date,
        preferred_delivery_time_slot: formData.preferred_delivery_time_slot || formData.preferred_pickup_time_slot,
        delivery_provider_id: selectedProvider?.id,
        use_provider_delivery: useProviderDelivery,
        special_instructions: formData.special_instructions,
        items_description: formData.items_description,
      };

      const response = await axios.post(`${API_URL}/api/delivery/orders`, orderData);
      
      if (response.data.success) {
        onSuccess?.(response.data.order);
        setOpen(false);
        setStep(1);
      }
    } catch (err) {
      console.error('Failed to create delivery order:', err);
      alert('Failed to create delivery order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const updateAddress = (field, value) => {
    setFormData(prev => ({
      ...prev,
      pickup_address: {
        ...prev.pickup_address,
        [field]: value,
      },
    }));
  };

  // Get tomorrow's date as minimum
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split('T')[0];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button className="bg-cyan-600 hover:bg-cyan-700">
            <Truck className="h-4 w-4 mr-2" />
            Schedule Pickup & Delivery
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5 text-cyan-500" />
            {step === 1 && 'Pickup & Delivery Details'}
            {step === 2 && 'Choose Delivery Partner'}
            {step === 3 && 'Confirm Order'}
          </DialogTitle>
          <DialogDescription>
            {step === 1 && 'Enter your address and preferred time for pickup'}
            {step === 2 && 'Select a delivery service or use provider delivery'}
            {step === 3 && 'Review and confirm your delivery order'}
          </DialogDescription>
        </DialogHeader>

        {/* Step indicators */}
        <div className="flex items-center justify-center gap-2 py-2">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`h-2 w-8 rounded-full ${s <= step ? 'bg-cyan-500' : 'bg-gray-200'}`}
            />
          ))}
        </div>

        {/* Step 1: Address & Time */}
        {step === 1 && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Delivery Type</Label>
              <Select
                value={formData.delivery_type}
                onValueChange={(v) => updateFormData('delivery_type', v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pickup_and_delivery">Pickup & Delivery</SelectItem>
                  <SelectItem value="pickup_only">Pickup Only (I&apos;ll collect)</SelectItem>
                  <SelectItem value="delivery_only">Delivery Only (I&apos;ll drop off)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Contact Name *</Label>
                <Input
                  placeholder="Your name"
                  value={formData.pickup_address.contact_name}
                  onChange={(e) => updateAddress('contact_name', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Phone Number *</Label>
                <Input
                  placeholder="07X XXX XXXX"
                  value={formData.pickup_address.contact_phone}
                  onChange={(e) => updateAddress('contact_phone', e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Address Line 1 *</Label>
              <Input
                placeholder="House/Apartment number, Street"
                value={formData.pickup_address.address_line1}
                onChange={(e) => updateAddress('address_line1', e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Address Line 2</Label>
              <Input
                placeholder="Building name, Floor (optional)"
                value={formData.pickup_address.address_line2}
                onChange={(e) => updateAddress('address_line2', e.target.value)}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>City *</Label>
                <Input
                  placeholder="City"
                  value={formData.pickup_address.city}
                  onChange={(e) => updateAddress('city', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Landmark</Label>
                <Input
                  placeholder="Near..."
                  value={formData.pickup_address.landmark}
                  onChange={(e) => updateAddress('landmark', e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Pickup Date *</Label>
                <Input
                  type="date"
                  min={minDate}
                  value={formData.preferred_pickup_date}
                  onChange={(e) => updateFormData('preferred_pickup_date', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Time Slot *</Label>
                <Select
                  value={formData.preferred_pickup_time_slot}
                  onValueChange={(v) => updateFormData('preferred_pickup_time_slot', v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select time" />
                  </SelectTrigger>
                  <SelectContent>
                    {timeSlots.length > 0 ? (
                      timeSlots.map((slot) => (
                        <SelectItem key={slot.id} value={slot.id} disabled={!slot.available}>
                          {slot.label} ({slot.time})
                        </SelectItem>
                      ))
                    ) : (
                      <>
                        <SelectItem value="morning">Morning (8AM - 12PM)</SelectItem>
                        <SelectItem value="afternoon">Afternoon (12PM - 4PM)</SelectItem>
                        <SelectItem value="evening">Evening (4PM - 8PM)</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Items Description</Label>
              <Textarea
                placeholder="e.g., 5 shirts, 2 trousers, 1 suit..."
                value={formData.items_description}
                onChange={(e) => updateFormData('items_description', e.target.value)}
              />
            </div>

            <Button 
              className="w-full bg-cyan-600 hover:bg-cyan-700"
              onClick={() => setStep(2)}
              disabled={!formData.pickup_address.contact_name || !formData.pickup_address.contact_phone || !formData.pickup_address.address_line1 || !formData.preferred_pickup_date}
            >
              Continue
            </Button>
          </div>
        )}

        {/* Step 2: Delivery Partner */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="flex gap-2 mb-4">
              <Button
                variant={useProviderDelivery ? 'default' : 'outline'}
                className={useProviderDelivery ? 'bg-cyan-600' : ''}
                onClick={() => setUseProviderDelivery(true)}
              >
                Provider Delivery
              </Button>
              <Button
                variant={!useProviderDelivery ? 'default' : 'outline'}
                className={!useProviderDelivery ? 'bg-cyan-600' : ''}
                onClick={() => setUseProviderDelivery(false)}
              >
                Third-Party Partner
              </Button>
            </div>

            {useProviderDelivery ? (
              <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Truck className="h-8 w-8 text-cyan-600" />
                  <div>
                    <h4 className="font-medium">Provider&apos;s Own Delivery</h4>
                    <p className="text-sm text-gray-600 mt-1">
                      {laundryProvider?.provider_name || 'The laundry service'} will handle pickup and delivery using their own team.
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge className="bg-green-100 text-green-700">Free Pickup</Badge>
                      <Badge className="bg-blue-100 text-blue-700">Insured</Badge>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <DeliveryProviderSelector
                onSelect={setSelectedProvider}
                selectedId={selectedProvider?.id}
              />
            )}

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
                Back
              </Button>
              <Button 
                className="flex-1 bg-cyan-600 hover:bg-cyan-700"
                onClick={() => setStep(3)}
                disabled={!useProviderDelivery && !selectedProvider}
              >
                Continue
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Confirmation */}
        {step === 3 && (
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <h4 className="font-medium">Order Summary</h4>
              
              <div className="flex items-start gap-2">
                <MapPin className="h-4 w-4 text-gray-400 mt-1" />
                <div>
                  <p className="text-sm font-medium">Pickup Address</p>
                  <p className="text-sm text-gray-600">
                    {formData.pickup_address.address_line1}
                    {formData.pickup_address.address_line2 && `, ${formData.pickup_address.address_line2}`}
                    , {formData.pickup_address.city}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-2">
                <Calendar className="h-4 w-4 text-gray-400 mt-1" />
                <div>
                  <p className="text-sm font-medium">Pickup Time</p>
                  <p className="text-sm text-gray-600">
                    {formData.preferred_pickup_date} - {formData.preferred_pickup_time_slot?.replace('_', ' ')}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-2">
                <Truck className="h-4 w-4 text-gray-400 mt-1" />
                <div>
                  <p className="text-sm font-medium">Delivery Partner</p>
                  <p className="text-sm text-gray-600">
                    {useProviderDelivery 
                      ? `${laundryProvider?.provider_name || 'Provider'}'s Own Delivery`
                      : selectedProvider?.provider_name
                    }
                  </p>
                </div>
              </div>

              {formData.items_description && (
                <div className="flex items-start gap-2">
                  <Package className="h-4 w-4 text-gray-400 mt-1" />
                  <div>
                    <p className="text-sm font-medium">Items</p>
                    <p className="text-sm text-gray-600">{formData.items_description}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Special Instructions (Optional)</Label>
              <Textarea
                placeholder="Any special instructions for pickup/delivery..."
                value={formData.special_instructions}
                onChange={(e) => updateFormData('special_instructions', e.target.value)}
              />
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(2)} className="flex-1">
                Back
              </Button>
              <Button 
                className="flex-1 bg-cyan-600 hover:bg-cyan-700"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Confirm Order'
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DeliveryBookingDialog;
